import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, Search, Copy, Check } from 'lucide-react';
import { explorerClient, formatHep, truncateAddress, timeAgo, fullDate } from '@/utils/explorerClient';
import { HEP_TOKEN_ADDRESS, HEP_TOKEN_ABI } from '@/contracts/HepToken';
import { config } from '@/config';
import { errorLog } from '@/utils/logger';

// ─── Types ────────────────────────────────────────────────────────────────────

type ViewKind = 'home' | 'block' | 'tx' | 'address';

interface HomeData {
  latestBlock: bigint;
  chainId: number;
  blocks: BlockSummary[];
  recentTxs: TxSummary[];
}

interface BlockSummary {
  number: bigint;
  hash: string;
  timestamp: bigint;
  miner: string;
  txCount: number;
}

interface TxSummary {
  hash: string;
  from: string;
  to: string | null;
  value: bigint;
  blockNumber: bigint;
}

interface BlockDetail {
  number: bigint;
  hash: string;
  parentHash: string;
  timestamp: bigint;
  miner: string;
  gasUsed: bigint;
  gasLimit: bigint;
  txHashes: string[];
}

interface TxDetail {
  hash: string;
  blockNumber: bigint | null;
  from: string;
  to: string | null;
  value: bigint;
  gas: bigint;
  gasUsed: bigint;
  status: 'success' | 'reverted';
}

interface AddressDetail {
  address: string;
  balance: bigint;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function classifySearch(raw: string): { kind: 'tx'; hash: `0x${string}` } | { kind: 'address'; address: `0x${string}` } | { kind: 'block'; number: bigint } | null {
  const t = raw.trim();
  if (/^0x[0-9a-fA-F]{64}$/.test(t)) return { kind: 'tx', hash: t as `0x${string}` };
  if (/^0x[0-9a-fA-F]{40}$/.test(t)) return { kind: 'address', address: t as `0x${string}` };
  if (/^\d+$/.test(t)) return { kind: 'block', number: BigInt(t) };
  return null;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <button
      onClick={handleCopy}
      className="ml-1 text-gray-500 hover:text-yellow-400 transition-colors"
      title="Copy to clipboard"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
};

const LoadingView: React.FC = () => (
  <div className="flex justify-center items-center py-16">
    <div className="h-8 w-8 rounded-full border-2 border-gray-600 border-b-yellow-400 animate-spin" />
  </div>
);

const ErrorView: React.FC<{ message: string }> = ({ message }) => (
  <div className="bg-red-900/30 border border-red-700/50 rounded-lg p-4 text-red-400">
    {message}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const ExplorerPage: React.FC = () => {
  const [searchParams] = useSearchParams();

  const [viewKind, setViewKind] = useState<ViewKind>('home');
  const [searchInput, setSearchInput] = useState('');
  const [searchError, setSearchError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Data states
  const [homeData, setHomeData] = useState<HomeData | null>(null);
  const [blockData, setBlockData] = useState<BlockDetail | null>(null);
  const [txData, setTxData] = useState<TxDetail | null>(null);
  const [addressData, setAddressData] = useState<AddressDetail | null>(null);

  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Data fetchers ──────────────────────────────────────────────────────────

  const fetchHome = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const [latestBlock, chainId] = await Promise.all([
        explorerClient.getBlockNumber(),
        explorerClient.getChainId(),
      ]);

      const blockNumbers = Array.from({ length: 10 }, (_, i) => latestBlock - BigInt(i)).filter(n => n >= 0n);
      const blockResults = await Promise.all(
        blockNumbers.map(n => explorerClient.getBlock({ blockNumber: n, includeTransactions: true }))
      );

      const blocks: BlockSummary[] = blockResults.map(b => ({
        number: b.number ?? 0n,
        hash: b.hash ?? '',
        timestamp: b.timestamp,
        miner: b.miner,
        txCount: b.transactions.length,
      }));

      const recentTxs: TxSummary[] = blockResults
        .flatMap(b =>
          (b.transactions as any[]).map((tx: any) => ({
            hash: tx.hash,
            from: tx.from,
            to: tx.to,
            value: tx.value,
            blockNumber: b.number ?? 0n,
          }))
        )
        .slice(0, 20);

      setHomeData({ latestBlock, chainId, blocks, recentTxs });
    } catch (err: any) {
      errorLog('Explorer fetchHome error:', err);
      setError('Failed to load blockchain data. Check your RPC connection.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchBlock = useCallback(async (blockNumber: bigint) => {
    setIsLoading(true);
    setError('');
    try {
      const b = await explorerClient.getBlock({ blockNumber, includeTransactions: true });
      setBlockData({
        number: b.number ?? blockNumber,
        hash: b.hash ?? '',
        parentHash: b.parentHash,
        timestamp: b.timestamp,
        miner: b.miner,
        gasUsed: b.gasUsed,
        gasLimit: b.gasLimit,
        txHashes: (b.transactions as any[]).map((tx: any) => typeof tx === 'string' ? tx : tx.hash),
      });
      setViewKind('block');
    } catch (err: any) {
      errorLog('Explorer fetchBlock error:', err);
      setError(`Failed to load block #${blockNumber}.`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchTx = useCallback(async (hash: `0x${string}`) => {
    setIsLoading(true);
    setError('');
    try {
      const [tx, receipt] = await Promise.all([
        explorerClient.getTransaction({ hash }),
        explorerClient.getTransactionReceipt({ hash }),
      ]);
      setTxData({
        hash: tx.hash,
        blockNumber: tx.blockNumber,
        from: tx.from,
        to: tx.to ?? null,
        value: tx.value,
        gas: tx.gas,
        gasUsed: receipt.gasUsed,
        status: receipt.status,
      });
      setViewKind('tx');
    } catch (err: any) {
      errorLog('Explorer fetchTx error:', err);
      setError(`Transaction not found: ${hash}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchAddress = useCallback(async (address: `0x${string}`) => {
    setIsLoading(true);
    setError('');
    try {
      const balance = await explorerClient.readContract({
        address: HEP_TOKEN_ADDRESS,
        abi: HEP_TOKEN_ABI,
        functionName: 'balanceOf',
        args: [address],
      }) as bigint;
      setAddressData({ address, balance });
      setViewKind('address');
    } catch (err: any) {
      errorLog('Explorer fetchAddress error:', err);
      setError(`Failed to load address data for ${address}.`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ── Auto-refresh (home view only) ──────────────────────────────────────────

  useEffect(() => {
    if (viewKind === 'home') {
      refreshTimerRef.current = setInterval(fetchHome, 15000);
    } else {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    }
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [viewKind, fetchHome]);

  // ── URL param handling on mount ────────────────────────────────────────────

  useEffect(() => {
    const hash    = searchParams.get('hash');
    const block   = searchParams.get('block');
    const addr    = searchParams.get('address');
    if (hash)  fetchTx(hash as `0x${string}`);
    else if (block) fetchBlock(BigInt(block));
    else if (addr)  fetchAddress(addr as `0x${string}`);
    else fetchHome();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Search ─────────────────────────────────────────────────────────────────

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchError('');
    const classified = classifySearch(searchInput);
    if (!classified) {
      setSearchError('Enter a block number, transaction hash (0x + 64 chars), or address (0x + 40 chars).');
      return;
    }
    if (classified.kind === 'tx')      fetchTx(classified.hash);
    else if (classified.kind === 'block')   fetchBlock(classified.number);
    else if (classified.kind === 'address') fetchAddress(classified.address);
  };

  const goHome = () => {
    setViewKind('home');
    setBlockData(null);
    setTxData(null);
    setAddressData(null);
    fetchHome();
  };

  // ── Click handlers ─────────────────────────────────────────────────────────

  const onBlockClick = (n: bigint) => fetchBlock(n);
  const onTxClick    = (h: string) => fetchTx(h as `0x${string}`);
  const onAddrClick  = (a: string) => fetchAddress(a as `0x${string}`);

  // ── Render helpers ─────────────────────────────────────────────────────────

  const BackButton = () => (
    <button
      onClick={goHome}
      className="flex items-center gap-1 text-gray-400 hover:text-yellow-400 transition-colors mb-6"
    >
      <ArrowLeft className="h-4 w-4" />
      Back to Explorer
    </button>
  );

  const HashCell: React.FC<{ value: string; onClick?: () => void; mono?: boolean }> = ({ value, onClick, mono = true }) => (
    <span className="inline-flex items-center gap-0.5">
      <span
        className={`${mono ? 'font-mono' : ''} ${onClick ? 'text-yellow-400 hover:underline cursor-pointer' : 'text-gray-300'}`}
        onClick={onClick}
        title={value}
      >
        {truncateAddress(value)}
      </span>
      <CopyButton text={value} />
    </span>
  );

  // ── Views ──────────────────────────────────────────────────────────────────

  const renderHome = () => {
    if (isLoading && !homeData) return <LoadingView />;
    if (error && !homeData) return <ErrorView message={error} />;
    if (!homeData) return null;
    const { latestBlock, chainId, blocks, recentTxs } = homeData;

    return (
      <>
        {/* Stats cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-[#2a2a2a] rounded-lg p-6">
            <p className="text-gray-400 text-sm mb-1">Latest Block</p>
            <p className="text-2xl font-bold text-yellow-400">#{latestBlock.toString()}</p>
          </div>
          <div className="bg-[#2a2a2a] rounded-lg p-6">
            <p className="text-gray-400 text-sm mb-1">Chain ID</p>
            <p className="text-2xl font-bold text-white">{chainId}</p>
          </div>
          <div className="bg-[#2a2a2a] rounded-lg p-6">
            <p className="text-gray-400 text-sm mb-1">Network</p>
            <p className="text-2xl font-bold text-white">Heperum</p>
          </div>
        </div>

        {/* Recent Blocks */}
        <div className="bg-[#2a2a2a] rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">Recent Blocks</h2>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left text-gray-400 pb-2 pr-4">Block</th>
                  <th className="text-left text-gray-400 pb-2 pr-4">Age</th>
                  <th className="text-left text-gray-400 pb-2 pr-4">Miner</th>
                  <th className="text-right text-gray-400 pb-2">Txns</th>
                </tr>
              </thead>
              <tbody>
                {blocks.map(b => (
                  <tr key={b.number.toString()} className="border-b border-gray-800 hover:bg-gray-800/40 transition-colors">
                    <td className="py-2 pr-4">
                      <button
                        onClick={() => onBlockClick(b.number)}
                        className="text-yellow-400 hover:underline font-mono"
                      >
                        #{b.number.toString()}
                      </button>
                    </td>
                    <td className="py-2 pr-4 text-gray-300" title={fullDate(b.timestamp)}>
                      {timeAgo(b.timestamp)}
                    </td>
                    <td className="py-2 pr-4">
                      <HashCell value={b.miner} onClick={() => onAddrClick(b.miner)} />
                    </td>
                    <td className="py-2 text-right text-gray-300">{b.txCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {blocks.map(b => (
              <div key={b.number.toString()} className="bg-[#333] rounded p-3 text-sm">
                <div className="flex justify-between mb-1">
                  <button onClick={() => onBlockClick(b.number)} className="text-yellow-400 hover:underline font-mono font-semibold">
                    #{b.number.toString()}
                  </button>
                  <span className="text-gray-400" title={fullDate(b.timestamp)}>{timeAgo(b.timestamp)}</span>
                </div>
                <div className="text-gray-400 text-xs">Miner: <HashCell value={b.miner} onClick={() => onAddrClick(b.miner)} /></div>
                <div className="text-gray-400 text-xs">{b.txCount} txn{b.txCount !== 1 ? 's' : ''}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-[#2a2a2a] rounded-lg p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Recent Transactions</h2>

          {recentTxs.length === 0 ? (
            <p className="text-gray-500 text-sm">No transactions in the last 10 blocks.</p>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left text-gray-400 pb-2 pr-4">Tx Hash</th>
                      <th className="text-left text-gray-400 pb-2 pr-4">Block</th>
                      <th className="text-left text-gray-400 pb-2 pr-4">From</th>
                      <th className="text-left text-gray-400 pb-2 pr-4">To</th>
                      <th className="text-right text-gray-400 pb-2">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentTxs.map(tx => (
                      <tr key={tx.hash} className="border-b border-gray-800 hover:bg-gray-800/40 transition-colors">
                        <td className="py-2 pr-4">
                          <HashCell value={tx.hash} onClick={() => onTxClick(tx.hash)} />
                        </td>
                        <td className="py-2 pr-4">
                          <button onClick={() => onBlockClick(tx.blockNumber)} className="text-yellow-400 hover:underline font-mono">
                            #{tx.blockNumber.toString()}
                          </button>
                        </td>
                        <td className="py-2 pr-4">
                          <HashCell value={tx.from} onClick={() => onAddrClick(tx.from)} />
                        </td>
                        <td className="py-2 pr-4">
                          {tx.to ? <HashCell value={tx.to} onClick={() => onAddrClick(tx.to!)} /> : <span className="text-gray-500">Contract creation</span>}
                        </td>
                        <td className="py-2 text-right text-gray-300 font-mono">{formatHep(tx.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-2">
                {recentTxs.map(tx => (
                  <div key={tx.hash} className="bg-[#333] rounded p-3 text-xs">
                    <div className="mb-1">
                      <span className="text-gray-400">Tx: </span>
                      <HashCell value={tx.hash} onClick={() => onTxClick(tx.hash)} />
                    </div>
                    <div className="mb-1 text-gray-400">
                      Block: <button onClick={() => onBlockClick(tx.blockNumber)} className="text-yellow-400 hover:underline font-mono">#{tx.blockNumber.toString()}</button>
                    </div>
                    <div className="mb-1 text-gray-400">From: <HashCell value={tx.from} onClick={() => onAddrClick(tx.from)} /></div>
                    {tx.to && <div className="mb-1 text-gray-400">To: <HashCell value={tx.to} onClick={() => onAddrClick(tx.to!)} /></div>}
                    <div className="text-gray-300 font-mono">{formatHep(tx.value)}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {isLoading && homeData && (
          <div className="text-center mt-4 text-gray-500 text-sm">Refreshing...</div>
        )}
      </>
    );
  };

  const renderBlock = () => {
    if (isLoading) return <LoadingView />;
    if (error) return <><BackButton /><ErrorView message={error} /></>;
    if (!blockData) return null;

    return (
      <>
        <BackButton />
        <div className="bg-[#2a2a2a] rounded-lg p-6">
          <h2 className="text-xl font-bold text-white mb-6">Block #{blockData.number.toString()}</h2>
          <dl className="space-y-4">
            <DetailRow label="Hash">
              <span className="font-mono text-gray-300 break-all">{blockData.hash}</span>
              <CopyButton text={blockData.hash} />
            </DetailRow>
            <DetailRow label="Parent Hash">
              <span className="font-mono text-gray-300 break-all">{blockData.parentHash}</span>
              <CopyButton text={blockData.parentHash} />
            </DetailRow>
            <DetailRow label="Timestamp">
              <span className="text-gray-300" title={fullDate(blockData.timestamp)}>
                {fullDate(blockData.timestamp)} ({timeAgo(blockData.timestamp)})
              </span>
            </DetailRow>
            <DetailRow label="Miner">
              <button onClick={() => onAddrClick(blockData.miner)} className="font-mono text-yellow-400 hover:underline break-all">
                {blockData.miner}
              </button>
              <CopyButton text={blockData.miner} />
            </DetailRow>
            <DetailRow label="Gas Used">
              <span className="text-gray-300">{blockData.gasUsed.toString()} / {blockData.gasLimit.toString()}</span>
            </DetailRow>
            <DetailRow label="Transactions">
              <span className="text-gray-300">{blockData.txHashes.length}</span>
            </DetailRow>
          </dl>

          {blockData.txHashes.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wide">Transactions in this block</h3>
              <div className="space-y-1.5">
                {blockData.txHashes.map(h => (
                  <div key={h} className="flex items-center gap-1">
                    <button onClick={() => onTxClick(h)} className="font-mono text-yellow-400 hover:underline text-sm break-all">
                      {h}
                    </button>
                    <CopyButton text={h} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderTx = () => {
    if (isLoading) return <LoadingView />;
    if (error) return <><BackButton /><ErrorView message={error} /></>;
    if (!txData) return null;

    return (
      <>
        <BackButton />
        <div className="bg-[#2a2a2a] rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <h2 className="text-xl font-bold text-white">Transaction</h2>
            {txData.status === 'success' ? (
              <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-900/50 text-green-400 border border-green-700/50">Success</span>
            ) : (
              <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-900/50 text-red-400 border border-red-700/50">Reverted</span>
            )}
          </div>
          <dl className="space-y-4">
            <DetailRow label="Hash">
              <span className="font-mono text-gray-300 break-all">{txData.hash}</span>
              <CopyButton text={txData.hash} />
            </DetailRow>
            <DetailRow label="Block">
              {txData.blockNumber != null ? (
                <button onClick={() => onBlockClick(txData.blockNumber!)} className="font-mono text-yellow-400 hover:underline">
                  #{txData.blockNumber.toString()}
                </button>
              ) : (
                <span className="text-gray-500">Pending</span>
              )}
            </DetailRow>
            <DetailRow label="From">
              <button onClick={() => onAddrClick(txData.from)} className="font-mono text-yellow-400 hover:underline break-all">
                {txData.from}
              </button>
              <CopyButton text={txData.from} />
            </DetailRow>
            <DetailRow label="To">
              {txData.to ? (
                <>
                  <button onClick={() => onAddrClick(txData.to!)} className="font-mono text-yellow-400 hover:underline break-all">
                    {txData.to}
                  </button>
                  <CopyButton text={txData.to} />
                </>
              ) : (
                <span className="text-gray-500">Contract creation</span>
              )}
            </DetailRow>
            <DetailRow label="Value">
              <span className="text-gray-300 font-mono">{formatHep(txData.value)}</span>
            </DetailRow>
            <DetailRow label="Gas Used">
              <span className="text-gray-300">{txData.gasUsed.toString()} / {txData.gas.toString()}</span>
            </DetailRow>
          </dl>
        </div>
      </>
    );
  };

  const renderAddress = () => {
    if (isLoading) return <LoadingView />;
    if (error) return <><BackButton /><ErrorView message={error} /></>;
    if (!addressData) return null;

    return (
      <>
        <BackButton />
        <div className="bg-[#2a2a2a] rounded-lg p-6">
          <h2 className="text-xl font-bold text-white mb-6">Address</h2>
          <dl className="space-y-4">
            <DetailRow label="Address">
              <span className="font-mono text-gray-300 break-all">{addressData.address}</span>
              <CopyButton text={addressData.address} />
            </DetailRow>
            <DetailRow label="HEP Balance">
              <span className="text-gray-300 font-mono text-lg">{formatHep(addressData.balance)}</span>
            </DetailRow>
          </dl>

          <div className="mt-6 p-4 bg-[#1e1e1e] rounded-lg border border-gray-700">
            <p className="text-gray-400 text-sm">
              For full transaction history of this address, use{' '}
              <a
                href={`${config.blockchain.explorerUrl}/address/${addressData.address}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-yellow-400 hover:underline"
              >
                Blockscout
              </a>
              .
            </p>
          </div>
        </div>
      </>
    );
  };

  // ── Layout ─────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-5xl mx-auto">
      {/* Page title */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Block Explorer</h1>
        <p className="text-gray-400 text-sm mt-1">Browse blocks, transactions, and addresses on the HEP chain</p>
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex gap-2">
          <input
            type="text"
            value={searchInput}
            onChange={e => { setSearchInput(e.target.value); setSearchError(''); }}
            placeholder="Search block number, tx hash, or address..."
            className="flex-1 bg-[#2a2a2a] border border-gray-700 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400 font-mono text-sm"
          />
          <button
            type="submit"
            className="heperum-btn-gold px-4 py-2.5"
          >
            <Search className="h-4 w-4" />
          </button>
        </div>
        {searchError && <p className="mt-1.5 text-red-400 text-sm">{searchError}</p>}
      </form>

      {/* View content */}
      {viewKind === 'home'    && renderHome()}
      {viewKind === 'block'   && renderBlock()}
      {viewKind === 'tx'      && renderTx()}
      {viewKind === 'address' && renderAddress()}
    </div>
  );
};

// ─── Detail row helper ────────────────────────────────────────────────────────

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-1 sm:gap-4 border-b border-gray-800 pb-3">
    <dt className="text-gray-400 text-sm font-medium">{label}</dt>
    <dd className="text-sm flex items-start gap-1 flex-wrap">{children}</dd>
  </div>
);

export default ExplorerPage;
